import * as React from 'react';
import { capabilities as projectsCapabilities } from '@sharefiledev/projects-pilet-sdk';
import {
	ProvideResourceIdOptions,
	ResourceNavigationCapability,
} from '@sharefiledev/sfplugin-capability-contracts';
import { RID } from '@sharefiledev/sfplugin-resource-id';
import { useAsyncCallback } from 'react-async-hook';
import { PermissionResolver } from '../Common/permissions';
import { logger } from '../logger';
import { usePluginDataProviderContext } from '../PluginDataProvider/PluginDataProviderContext';
import {
	PermissionFailureReason,
	PermissionPolicy,
	RequestListDto,
	RequestListStatus,
	RequestListStatusCount,
	RequestListTaskStatus,
} from '../types';

export enum RenderMode {
	default,
	specEdit,
}

const Context = React.createContext<RequestListContext>({
	requestList: undefined as any,
	provideResourceId: () => undefined as any,
	getNavigationDetails: () => undefined as any,
	setRequestList: () => {},
	updateTaskSummary: () => {},
	moveItemStatus: () => {},
	fetchTaskSummary: () => {},
	isCompleted: () => false,
	isInProgress: () => false,
	isNotSent: () => false,
	renderMode: RenderMode.default,
	originAnalyticsTrackingId: '',
});

export interface RequestListContext {
	requestList: RequestListDto;
	provideResourceId(options?: ProvideResourceIdOptions): Promise<string>;
	getNavigationDetails: ResourceNavigationCapability['getNavigationDetails'];
	setRequestList(requestList: RequestListDto): void;
	updateTaskSummary(statusDeltaMap: RequestListStatusCount, totalDelta: number): void;
	moveItemStatus(from: RequestListTaskStatus, to: RequestListTaskStatus): void;
	fetchTaskSummary(): void;
	isCompleted(): boolean;
	isInProgress(): boolean;
	isNotSent(): boolean;
	renderMode: RenderMode;
	originAnalyticsTrackingId?: string;
}

interface RequestListContextProviderProps {
	requestList: RequestListDto;
	renderMode?: RenderMode;
	originAnalyticsTrackingId?: string;
}

export function RequestListContextProvider(
	props: React.PropsWithChildren<RequestListContextProviderProps>
) {
	const dataProvider = usePluginDataProviderContext();
	const [requestList, setRequestListState] = React.useState(props.requestList);
	React.useEffect(() => {
		setRequestList(props.requestList);
	}, [props.requestList]);

	const { execute: fetchTaskSummary } = useAsyncCallback(
		async () => {
			const result = await dataProvider.getRequestList(props.requestList.requestListId);
			setRequestListState(state => ({ ...state, taskSummary: result.taskSummary }));
		},
		{
			onError(e) {
				logger.logError(e);
			},
		}
	);

	const destinationRID = RID.parse(requestList.destinationId);
	const capabilities = projectsCapabilities[destinationRID.entityType];

	return (
		<Context.Provider
			value={{
				requestList,
				getNavigationDetails:
					capabilities?.getNavigationDetails ?? (() => Promise.resolve({})),
				setRequestList,
				updateTaskSummary,
				moveItemStatus,
				fetchTaskSummary,
				isCompleted: () => requestList.status === RequestListStatus.Completed,
				isInProgress: () => requestList.status === RequestListStatus.InProgress,
				isNotSent: () => requestList.status === RequestListStatus.NotSent,
				provideResourceId: options => {
					if (!capabilities?.provideResourceId) {
						throw new Error(`provideResourceId not found for ${requestList.resourceId}`);
					}
					return capabilities.provideResourceId(requestList.resourceId, options);
				},
				renderMode: props.renderMode ?? RenderMode.default,
				originAnalyticsTrackingId: props.originAnalyticsTrackingId,
			}}
		>
			{props.children}
		</Context.Provider>
	);

	function setRequestList(requestListDto: RequestListDto) {
		setRequestListState(reComputePermissions(requestListDto));
	}

	function updateTaskSummary(statusDeltaMap: RequestListStatusCount, totalDelta: number) {
		// taskSummary can be undefined for users without read permission on Document request, do not update in those cases
		if (!requestList.taskSummary) {
			return;
		}
		const statusCount = requestList.taskSummary?.statusCount;
		setRequestList({
			...requestList,
			taskSummary: {
				total: requestList.taskSummary!.total + totalDelta,
				statusCount: {
					NotSent: statusCount!.NotSent + statusDeltaMap.NotSent,
					InProgress: statusCount!.InProgress + statusDeltaMap.InProgress,
					InReview: statusCount!.InReview + statusDeltaMap.InReview,
					Completed: statusCount!.Completed + statusDeltaMap.Completed,
				},
			},
		});
	}

	function moveItemStatus(from: RequestListTaskStatus, to: RequestListTaskStatus) {
		// taskSummary can be undefined for users without read permission on Document request, do not update in those cases
		if (from === to || !requestList.taskSummary) {
			return;
		}
		setRequestListState(list => {
			const statusCount = { ...list.taskSummary!.statusCount };
			statusCount[from] = statusCount[from] - 1;
			statusCount[to] = statusCount[to] + 1;
			return { ...list, taskSummary: { ...list.taskSummary!, statusCount } };
		});
	}
}

export const useRequestListContext = () => React.useContext(Context);

function reComputePermissions(requestList: RequestListDto) {
	return PermissionResolver.create(requestList)
		.tryResolvePermission(
			PermissionPolicy.CanStartDocumentRequest,
			PermissionFailureReason.RequiresItemInList,
			currentState =>
				typeof currentState.taskSummary?.total === 'number'
					? !!currentState.taskSummary.total
					: currentState.permissions?.CanStartDocumentRequest?.enabled ?? false
		)
		.tryResolvePermission(
			PermissionPolicy.CanModifyDocumentRequest,
			PermissionFailureReason.RequiresNotCompletedList,
			currentState => currentState.status !== RequestListStatus.Completed
		)
		.tryResolvePermission(
			PermissionPolicy.CanAddItem,
			PermissionFailureReason.RequiresNotCompletedList,
			currentState => currentState.status !== RequestListStatus.Completed
		)
		.tryResolvePermission(
			PermissionPolicy.CanShareDocumentRequest,
			PermissionFailureReason.RequiresNotCompletedList,
			currentState => currentState.status !== RequestListStatus.Completed
		)
		.tryResolvePermission(
			PermissionPolicy.CanCloseOrOpenDocumentRequest,
			PermissionFailureReason.RequiresStartedList,
			currentState => currentState.status !== RequestListStatus.NotSent
		)
		.computeUpdatedPermissions();
}
